import type {EventHandler} from '@/redux/middleware/googleTagManager/types';
import type {ActionWithPayload} from '@/redux/typesNew';
import type {Page} from '@/types';

import {PAGE_HOTELS, PAGE_OFFERS} from '@/consts';
import {
  FILTER_DESTINATION_AIRPORTS,
  FILTER_AIRLINES,
  FILTER_BEACH,
  FILTER_BOARD_TYPE,
  FILTER_CHILDREN,
  FILTER_DEPARTURE_TIME,
  FILTER_DURATION,
  FILTER_ENTERTAINMENT,
  FILTER_FACILITIES,
  FILTER_FLIGHT_TRANSFER,
  FILTER_HOTEL_CATEGORY,
  FILTER_HOTEL_RATING,
  FILTER_HOTEL_TYPE,
  FILTER_POOL,
  FILTER_PRICE,
  FILTER_PROMOTION_FLEX,
  FILTER_PROMOTION_SAFETY,
  FILTER_PROMOTION_SPECIALS,
  FILTER_RESTAURANT,
  FILTER_RETURN_TIME,
  FILTER_ROOM_ATTRIBUTES,
  FILTER_ROOM_TYPE,
  FILTER_SPORTS,
  FILTER_SUSTAINABILITY,
  FILTER_TOUR_OPERATOR,
  FILTER_WATER_SPORTS,
  FILTER_WELLNESS,
  FILTER_DISTANCE_SKI_SLOPE,
  FILTER_DISTANCE_BEACH,
  FILTER_DISTANCE_CITY_CENTER,
  FILTER_DISTANCE_AIRPORT,
} from '@/modules/common/models/filters';
import {
  EVENT_FILTER_REMOVE,
  EVENT_FILTER_SET,
} from '@/redux/middleware/googleTagManager/actionTypes';
import {serializeFilterRating} from '@/redux/middleware/googleTagManager/utils/mapFilterParams';
import {formatMinutesAsTime} from '@/utils/date';

type Payload = {
  filters: Array<[string, unknown]>;
  popular?: boolean;
  currentPage: Page;
};

type Event = {
  event: 'filter';
  event_action: string;
  non_interaction: false;
};

// @TODO can we merge this somehow with src/redux/middleware/googleTagManager/utils/mapFilterParams.js?
const toTrackingParam = ([filterId, value]: [string, any]) => {
  // eslint-disable-next-line sonarjs/max-switch-cases -- because
  switch (filterId) {
    case FILTER_PRICE: {
      return [['search_price_max', value ?? '']];
    }
    case FILTER_DEPARTURE_TIME: {
      return [
        [
          'search_departure_departure_time_min',
          value && value[0] != null ? formatMinutesAsTime(value[0]) : '',
        ],
        [
          'search_departure_departure_time_max',
          value && value[1] != null ? formatMinutesAsTime(value[1]) : '',
        ],
      ];
    }
    case FILTER_RETURN_TIME: {
      return [
        [
          'search_arrival_departure_time_min',
          value && value[0] != null ? formatMinutesAsTime(value[0]) : '',
        ],
        [
          'search_arrival_departure_time_max',
          value && value[1] != null ? formatMinutesAsTime(value[1]) : '',
        ],
      ];
    }
    case FILTER_FLIGHT_TRANSFER: {
      return [['search_transfer', value.join(',')]];
    }
    case FILTER_BOARD_TYPE: {
      return [['search_board', value.join(',')]];
    }
    case FILTER_ROOM_TYPE: {
      return [['search_room', value.join(',')]];
    }
    case FILTER_ROOM_ATTRIBUTES: {
      return [['search_room_attributes', value.join(',')]];
    }
    case FILTER_TOUR_OPERATOR: {
      return [['search_organizer', value.join(',')]];
    }
    case FILTER_DURATION: {
      return [['search_duration_max', value.join(',')]];
    }
    case FILTER_PROMOTION_SPECIALS: {
      return [['search_organizer_promotion', value.join(',')]];
    }
    case FILTER_PROMOTION_FLEX: {
      return [['search_flex_option', value.join(',')]];
    }
    case FILTER_PROMOTION_SAFETY: {
      return [['search_safety_option', value.join(',')]];
    }
    case FILTER_FACILITIES: {
      return [['search_hotel_facilities', value.join(',')]];
    }
    case FILTER_CHILDREN: {
      return [['search_children_offers', value.join(',')]];
    }
    case FILTER_WELLNESS: {
      return [['search_wellness', value.join(',')]];
    }
    case FILTER_POOL: {
      return [['search_pool', value.join(',')]];
    }
    case FILTER_BEACH: {
      return [['search_beach', value.join(',')]];
    }
    case FILTER_SPORTS: {
      return [['search_sport', value.join(',')]];
    }
    case FILTER_WATER_SPORTS: {
      return [['search_watersport', value.join(',')]];
    }
    case FILTER_RESTAURANT: {
      return [['search_restaurant', value.join(',')]];
    }
    case FILTER_ENTERTAINMENT: {
      return [['search_entertainment', value.join(',')]];
    }
    case FILTER_HOTEL_CATEGORY: {
      return [['search_hotel_category', value == null ? '' : String(value)]];
    }
    case FILTER_HOTEL_RATING: {
      return [['search_user_rating', serializeFilterRating(value)]];
    }
    case FILTER_HOTEL_TYPE: {
      return [['search_hotel_type', value.join(',')]];
    }
    case FILTER_SUSTAINABILITY: {
      return [['search_sustainability', value.join(',')]];
    }
    case FILTER_AIRLINES: {
      return [['search_airlines', value.join(',')]];
    }
    case FILTER_DESTINATION_AIRPORTS: {
      return [['search_destination_airport', value.join(',')]];
    }
    case FILTER_DISTANCE_CITY_CENTER: {
      return [['search_distance_city_center', value]];
    }
    case FILTER_DISTANCE_AIRPORT: {
      return [['search_distance_airport', value]];
    }
    case FILTER_DISTANCE_SKI_SLOPE: {
      return [['search_distance_ski_slope', value]];
    }
    case FILTER_DISTANCE_BEACH: {
      return [['search_distance_beach', value]];
    }
    default: {
      return [];
    }
  }
};

export const handler: EventHandler<ActionWithPayload<any, Payload>, Event> = ({
  type,
  payload: {filters, popular, currentPage},
}) => {
  const trackingParams = Object.fromEntries(
    filters.flatMap((filter) => toTrackingParam(filter)).filter(Boolean)
  );

  return {
    event: 'filter',
    event_action: `${popular ? 'top_' : ''}filter_${
      // @ts-expect-error ...
      {[EVENT_FILTER_SET]: 'set', [EVENT_FILTER_REMOVE]: 'deleted'}[type]
    }_${
      {[PAGE_HOTELS]: 'hotel_list', [PAGE_OFFERS]: 'offer_list'}[currentPage]
    }`,
    ...trackingParams,
    non_interaction: false,
  };
};
